/**
 * Villes et départements de l'ANNUAIRE — source unique côté donoor.org
 * (serveur Nitro ET pages), lot 4 de l'internationalisation (2026-09-27).
 *
 * Contrat : docs/architecture/adresses-structurees.md, §9.
 *
 * POURQUOI CE FICHIER : trois analyseurs de ville divergeaient (`cityFromAddress`
 * de frenchGeo, `extractCityFromAddress` d'extract-city et de city-slug, et un
 * troisième dans l'image de partage). Une même association sortait donc sous
 * deux clés selon la page, et les homonymes (Saint-Denis 93 et Saint-Denis de
 * La Réunion, Saint-Pierre 974 / 975 / 972) partageaient la même page.
 *
 * Désormais, une seule lecture :
 *  1. les MORCEAUX enregistrés par l'API (`address_city`, `address_city_slug`,
 *     `address_department`, migration 307 ; `location_city_slug`… pour un
 *     lieu), calculés en base par les mêmes fonctions que `@donoor/utils` ;
 *  2. à défaut (ligne pas encore convertie, API d'avant 307 pendant la
 *     fenêtre de déploiement), l'analyseur unique `analyserAdresseLibre` +
 *     `slugVille` de `@donoor/utils`, qui donne EXACTEMENT la même clé.
 *
 * Clé de ville (`slugVille`) : métropole = slug seul (« saint-denis ») ;
 * outre-mer = slug + collectivité (« saint-denis-974 ») ; étranger = slug +
 * pays (« bruxelles-be »). Les anciennes clés (« fort-de-france », « barp »,
 * « tampon ») sont redirigées en 301 vers la nouvelle (`cleDeRedirection`).
 */
import {
  departementAnnuaireOrganisation,
  nomTerritoireOuPays,
  replierBlancs,
  slugTexte,
  TERRITOIRES_OUTRE_MER,
  villeAnnuaireOrganisation,
  villeDepuisTexte,
  type CodeTerritoire,
} from "@donoor/utils";
import { departmentByCode, type Department } from "./frenchGeo";

/**
 * Nombre de lignes lues par les pivots de l'annuaire (hubs, sitemaps) : le
 * MÊME partout. Deux plafonds différents (10 000 ici, 50 000 là) faisaient
 * qu'une association pouvait figurer au sitemap d'une ville sans apparaître
 * sur la page de cette ville. 50 000 = plafond de l'API publique (limite
 * Google d'URL par sitemap).
 */
export const LIMITE_LECTURE_ANNUAIRE = 50000;

/** Champs d'adresse d'une association lus par l'annuaire (tous facultatifs). */
export interface AdresseOrganisationLue {
  full_address?: string | null;
  /** Pays de l'association (migration 298). */
  country?: string | null;
  address_street?: string | null;
  address_postcode?: string | null;
  address_city?: string | null;
  address_country?: string | null;
  address_territory?: string | null;
  address_department?: string | null;
  address_city_slug?: string | null;
}

/** Champs de lieu d'un événement ou d'une adhésion lus par l'annuaire. */
export interface LieuLu {
  location_city?: string | null;
  location_country?: string | null;
  location_territory?: string | null;
  location_department?: string | null;
  location_city_slug?: string | null;
}

/** Ville d'une ligne de l'annuaire. */
export interface VilleAnnuaire {
  /** Clé d'URL : `/associations/ville/{cle}`. */
  cle: string;
  /** Nom de la commune (« Saint-Denis »). */
  ville: string;
  /** Nom AFFICHÉ, homonymes précisés (« Saint-Denis (La Réunion) »). */
  nom: string;
  /** « 93 », « 974 », « 2A »… ; null à l'étranger. */
  departement: string | null;
  /** Pays ISO (FR pour l'outre-mer). */
  pays: string;
  territoire: string | null;
}

function paysNormalise(pays: string | null | undefined): string {
  return (pays ?? "").trim().toUpperCase() || "FR";
}

/**
 * Nom affiché d'une ville (§9.1) : « Saint-Denis » en métropole,
 * « Saint-Denis (La Réunion) » outre-mer, « Bruxelles (Belgique) » à
 * l'étranger. Le territoire n'est pas répété quand il porte le nom de la
 * ville (« Saint-Barthélemy »).
 */
export function nomVilleAnnuaire({
  ville,
  pays,
  territoire,
}: {
  ville: string;
  pays?: string | null;
  territoire?: string | null;
}): string {
  const lieu = nomTerritoireOuPays({ pays, territoire });
  if (!lieu || slugTexte(lieu) === slugTexte(ville)) return ville;
  return `${ville} (${lieu})`;
}

function construire(
  ville: string,
  cle: string,
  departement: string | null,
  pays: string,
  territoire: string | null,
): VilleAnnuaire {
  return { cle, ville, nom: nomVilleAnnuaire({ ville, pays, territoire }), departement, pays, territoire };
}

/**
 * Ville d'une association : morceaux d'abord, sinon l'analyseur unique sur
 * `full_address` lu avec le pays de l'association. Null si aucune ville ne se
 * lit (« Chemin du Moulin »). Règle PARTAGÉE `villeAnnuaireOrganisation`
 * (@donoor/utils) : le filtre « ville » de l'API (`/api/campaigns/public`)
 * range une association sous EXACTEMENT la même clé que ces pages.
 */
export function villeOrganisation(org: AdresseOrganisationLue | null | undefined): VilleAnnuaire | null {
  const v = villeAnnuaireOrganisation(org);
  return v ? construire(v.ville, v.cle, v.departement, v.pays, v.territoire) : null;
}

/**
 * Ville d'un lieu (événement, adhésion) : clé structurée d'abord, sinon la
 * ville saisie (même repli que le filtre ville de l'API, `memeVille`).
 */
export function villeLieu(lieu: LieuLu | null | undefined): VilleAnnuaire | null {
  if (!lieu) return null;
  const ville = replierBlancs(lieu.location_city);
  if (!ville) return null;
  if (lieu.location_city_slug) {
    return construire(
      ville,
      lieu.location_city_slug,
      lieu.location_department ?? null,
      paysNormalise(lieu.location_country),
      lieu.location_territory ?? null,
    );
  }
  const cle = slugTexte(ville);
  if (!cle) return null;
  return construire(ville, cle, null, "FR", null);
}

/**
 * Code du département (ou de la collectivité d'outre-mer) d'une association.
 * Règle PARTAGÉE `departementAnnuaireOrganisation` (@donoor/utils), la même
 * que le filtre `?departement=` de l'API : morceaux d'abord, sinon l'analyse
 * du texte, territoire NOMMÉ compris (« Sainte-Luce, Martinique » → 972,
 * comme `adresse_departement` à 4 arguments en base). Toujours le même
 * département que `villeOrganisation` : une association rangée sous
 * `sainte-luce-972` figure aussi sur le hub « Martinique » et au sitemap.
 * Jamais déduit d'un code postal quand le pays n'est pas la France
 * (« 10117 Berlin, Allemagne » n'est pas dans l'Aube).
 */
export function codeDepartementOrganisation(org: AdresseOrganisationLue | null | undefined): string | null {
  return departementAnnuaireOrganisation(org);
}

/** Département de l'annuaire d'une association (null : étranger ou inconnu). */
export function departementOrganisation(org: AdresseOrganisationLue | null | undefined): Department | null {
  return departmentByCode(codeDepartementOrganisation(org));
}

// ─────────────────────────────────────────────────────────────────────────────
// Anciennes clés de ville → redirection 301
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Clés sous lesquelles une association a pu être publiée AVANT le lot 4.
 *
 * ⚠️ Sert UNIQUEMENT à retrouver les anciennes URL (déjà indexées par Google,
 * partagées) pour les rediriger en 301 : jamais à afficher ni à classer une
 * ville. Les deux premières reproduisent à l'identique les anciennes règles
 * (« tout ce qui suit le code postal », « jusqu'à la virgule ») ; la
 * troisième est l'analyseur unique (§9.1 du contrat) — elle couvre les
 * communes renommées par la BAN : « 33114 Barp » était publiée sous « barp »,
 * elle l'est désormais sous « le-barp ».
 */
export function clesVilleHistoriques(texte: string | null | undefined): Set<string> {
  const cles = new Set<string>();
  const t = texte ?? "";
  const ajouter = (v: string | null | undefined) => {
    const s = slugTexte(v);
    if (s) cles.add(s);
  };
  ajouter(villeDepuisTexte(t));
  const apresCodePostal = t.match(/\d{5}\s+(.+)$/)?.[1];
  ajouter(apresCodePostal);
  ajouter(apresCodePostal?.split(",")[0]);
  // Ancien repli : adresse d'un seul segment, sans aucun chiffre.
  const nu = t.trim();
  if (nu && !/\d/.test(nu) && !/[,;]/.test(nu)) ajouter(nu);
  return cles;
}

/** Anciennes clés d'un lieu : la ville saisie telle quelle, slugifiée. */
export function clesLieuHistoriques(lieu: LieuLu | null | undefined): Set<string> {
  const s = slugTexte(lieu?.location_city);
  return new Set(s ? [s] : []);
}

/**
 * Clé ACTUELLE vers laquelle rediriger une clé qui ne correspond plus à
 * aucune ligne (§9.1) : la clé demandée est cherchée parmi les anciennes clés
 * de chaque ligne ; si toutes les lignes trouvées partagent UNE seule clé
 * actuelle, c'est la cible. Sinon (aucune, ou plusieurs homonymes possibles)
 * → null : la page garde son comportement (vide, noindex).
 *
 * Couvre le suffixe ajouté (« fort-de-france » → « fort-de-france-972 ») et
 * la commune renommée (« tampon » → « le-tampon-974 »).
 */
export function cleDeRedirection<T>(
  cleDemandee: string,
  lignes: readonly T[],
  cleActuelle: (ligne: T) => string | null | undefined,
  anciennesCles: (ligne: T) => ReadonlySet<string>,
): string | null {
  if (!cleDemandee) return null;
  if (lignes.some((l) => cleActuelle(l) === cleDemandee)) return null;
  const cibles = new Set<string>();
  for (const ligne of lignes) {
    const cle = cleActuelle(ligne);
    if (cle && anciennesCles(ligne).has(cleDemandee)) cibles.add(cle);
  }
  if (cibles.size !== 1) return null;
  const [cible] = [...cibles];
  return cible && cible !== cleDemandee ? cible : null;
}

/** Redirection d'une clé de ville d'ASSOCIATION (hubs associations, cagnottes, boutiques). */
export function redirectionVilleOrganisation(
  cleDemandee: string,
  orgs: readonly AdresseOrganisationLue[],
): string | null {
  return cleDeRedirection(
    cleDemandee,
    orgs,
    (o) => villeOrganisation(o)?.cle,
    (o) => clesVilleHistoriques(o.full_address),
  );
}

/** Redirection d'une clé de ville de LIEU (hubs événements, adhésions). */
export function redirectionVilleLieu(cleDemandee: string, lieux: readonly LieuLu[]): string | null {
  return cleDeRedirection(cleDemandee, lieux, (l) => villeLieu(l)?.cle, clesLieuHistoriques);
}

/**
 * Nom lisible déduit d'une clé quand AUCUNE ligne ne la porte (page vide,
 * noindex) : « fort-de-france-972 » → « Fort De France (Martinique) ». Le nom
 * exact vient toujours des données quand il y en a.
 *
 * Seul le suffixe d'outre-mer (3 chiffres) est décodé : un suffixe de deux
 * lettres est ambigu (« saint-martin-de-re » est l'île de Ré, pas La Réunion),
 * la clé est alors rendue telle quelle.
 */
export function nomDepuisCleVille(cle: string): string {
  const enMots = (s: string) =>
    s
      .split("-")
      .filter(Boolean)
      .map((m) => m.charAt(0).toUpperCase() + m.slice(1))
      .join(" ");
  const m = cle.match(/^(.+)-(9[78]\d)$/);
  const territoire = m ? territoireDuDepartement(m[2]!) : null;
  if (!m || !territoire) return enMots(cle);
  const lieu = nomTerritoireOuPays({ pays: "FR", territoire });
  return lieu ? `${enMots(m[1]!)} (${lieu})` : enMots(m[1]!);
}

/** Territoire d'un code de collectivité (« 972 » → MQ), depuis la table UNIQUE. */
function territoireDuDepartement(code: string): CodeTerritoire | null {
  return TERRITOIRES_OUTRE_MER.find((t) => t.departement === code)?.code ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Liste des villes d'un pivot
// ─────────────────────────────────────────────────────────────────────────────

export interface VilleComptee {
  slug: string;
  name: string;
  count: number;
}

/**
 * Villes distinctes (clé → nom affiché) avec leur nombre de lignes, triées par
 * nombre décroissant puis par nom. Homonymes métropolitains (même clé) :
 * une seule entrée, la page les regroupe par département.
 */
export function compterVilles(villes: ReadonlyArray<VilleAnnuaire | null | undefined>): VilleComptee[] {
  const parCle = new Map<string, VilleComptee>();
  for (const v of villes) {
    if (!v) continue;
    const cur = parCle.get(v.cle);
    if (cur) cur.count += 1;
    else parCle.set(v.cle, { slug: v.cle, name: v.nom, count: 1 });
  }
  return [...parCle.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "fr"));
}
